/**
 * Crash capture -- the app's ONE crash pipeline: what a render crash (ChunkErrorBoundary) or an uncaught error /
 * rejection actually was, kept where the owner can copy it from the device. The fallback UI's "Copy details" and
 * the ?inkdebug Copy log read it. `installCrashCapture` (called from analytics initTracking) owns the app's only
 * window `error` and `unhandledrejection` listeners; a rejection is also reported to analytics.
 *
 * Privacy: no document / file names and no markup text. The message is the JS error's own text (truncated); stack
 * frames are reduced to `function @ file:line:col` with the URL's origin and path dropped; the component stack is
 * component names only (crashFrames.ts, the one stack parser). Analytics gets only closed slugs (error_kind), code
 * locations and an identifier-shaped component name -- never the message.
 */
import { logInput, readInputLog } from '@/core/ink/inputLog';
import { componentNames, deviceFrames, rejectionProps, type RejectionProps } from './crashFrames';

export type CrashRecord = {
    at: string;
    source: 'boundary' | 'error' | 'rejection';
    name: string;
    message: string;
    frames: string[];
    components: string[];
    route: string;
};

const KEY = 'pmt-last-crashes';
const KEEP = 5;

function route(): string {
    try {
        // ids, never file names: the local viewer's /local/<file> path is reduced to /local/*
        return window.location.pathname.replace(/^\/local\/.*/, '/local/*');
    } catch {
        return '';
    }
}

export function buildCrash(source: CrashRecord['source'], err: unknown, componentStack?: string | null): CrashRecord {
    const e = err instanceof Error ? err : null;
    const message = (e ? e.message : typeof err === 'string' ? err : (() => { try { return JSON.stringify(err); } catch { return String(err); } })()) ?? '';
    const frames = deviceFrames(e?.stack, message);
    return {
        at: new Date().toISOString(),
        source,
        name: e?.name ?? typeof err,
        message: String(message).slice(0, 300),
        frames,
        components: componentNames(componentStack),
        route: route(),
    };
}

export function recordCrash(source: CrashRecord['source'], err: unknown, componentStack?: string | null): CrashRecord {
    const rec = buildCrash(source, err, componentStack);
    try {
        const prev = readCrashes();
        sessionStorage.setItem(KEY, JSON.stringify([rec, ...prev].slice(0, KEEP)));
    } catch { /* storage may be full or blocked; the in-memory log still has it */ }
    logInput({ k: 'crash', ...rec });
    return rec;
}

export function readCrashes(): CrashRecord[] {
    try {
        const v = JSON.parse(sessionStorage.getItem(KEY) ?? '[]');
        return Array.isArray(v) ? v : [];
    } catch {
        return [];
    }
}

/** What "Copy details" puts on the clipboard: the crashes plus the input log (empty unless ?inkdebug was on). */
export function crashReport(): string {
    return JSON.stringify({
        crashes: readCrashes(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        viewport: typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio } : null,
        inputLog: readInputLog(),
    }, null, 1);
}

let installed = false;
/**
 * The app's one window `error` + `unhandledrejection` listener pair. Every uncaught error and rejection is recorded
 * (log + sessionStorage), not only boundary crashes; a rejection is also handed to `reportRejection` as the
 * promise_rejection_unhandled props. Injected (track.ts passes `track`): track.ts imports this module, and the
 * repo's module graph is acyclic (`pnpm check:cycles`).
 */
export function installCrashCapture(reportRejection: (props: RejectionProps) => void): void {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    window.addEventListener('error', (ev) => {
        if (ev.error || ev.message) recordCrash('error', ev.error ?? ev.message);
    });
    window.addEventListener('unhandledrejection', (ev) => {
        try {
            recordCrash('rejection', ev.reason);
        } finally {
            reportRejection(rejectionProps(ev.reason));
        }
    });
}
